import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import type { ReviewListRow } from "@/lib/api/training-simulator/training-simulator-review";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { formatJoinedDate } from "@/lib/training-simulator/utils";
import { getInitials } from "@/lib/utils";
import { Button } from "@somethings-ui/web";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { MessageMentor } from "./message-mentor";

type ReviewCategoryType = "in_progress" | "approved" | "needs_review";

interface ReviewCategoryProps {
  title: string;
  count: number;
  items: ReviewListRow[];
  type: ReviewCategoryType;
  selectedIds?: Set<string>;
  onToggleSelect?: (progressId: string) => void;
}

export function ReviewCategory({
  count,
  items,
  title,
  type,
  selectedIds,
  onToggleSelect,
}: ReviewCategoryProps) {
  return (
    <AccordionItem
      value={title}
      className="overflow-hidden rounded-md border border-surfaces-divider bg-surfaces-z0"
    >
      <AccordionTrigger className="group h-12 px-6 py-0 hover:no-underline [&>svg]:hidden">
        <div className="flex w-full items-center gap-3">
          <ChevronRight className="size-5 shrink-0 text-icon-primary transition-transform duration-200 group-data-[state=open]:rotate-90" />
          <span className="text-body-2 font-medium text-text-primary">
            {title}
          </span>
          <span className="ml-auto text-caption-2 text-text-primary opacity-50">
            {count}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="p-0">
        {items.map((item) => (
          <ReviewRow
            key={item.progressId}
            item={item}
            type={type}
            selected={selectedIds?.has(item.progressId) ?? false}
            onToggleSelect={onToggleSelect}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}

function ReviewRow({
  item,
  type,
  selected,
  onToggleSelect,
}: {
  item: ReviewListRow;
  type: ReviewCategoryType;
  selected: boolean;
  onToggleSelect?: (progressId: string) => void;
}) {
  const { url } = useSignedMedia(item.mentor.profile_picture_url);
  const navigate = useNavigate();
  const { track } = useTrackEvent();

  return (
    <div className="flex h-[101px] w-full items-center gap-4 border-t border-surfaces-divider px-6">
      {type === "needs_review" && (
        <Checkbox
          className="size-5 rounded-[5px]"
          checked={selected}
          onCheckedChange={() => onToggleSelect?.(item.progressId)}
        />
      )}
      <Avatar className="size-12 rounded-md">
        <AvatarImage src={url || ""} />
        <AvatarFallback className="rounded-md bg-surfaces-z4 text-body-3 text-text-secondary">
          {getInitials(item.mentor.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-2">
        <span className="text-header-5 text-text-primary">
          {item.mentor.name}
        </span>
        {item.joined_at && (
          <span className="text-body-3 text-text-secondary">
            joined {formatJoinedDate(item.joined_at)}
          </span>
        )}
      </div>
      {/* @todo - Implement both actions */}
      {type === "needs_review" ? (
        <Button
          variant="primary"
          size="sm"
          fullWidth={false}
          className="ml-auto"
          onClick={() => {
            track(PostHogEvents.SIMULATOR_LM_REVIEW_TRAINING_CLICK, {
              progress_id: item.progressId,
            });
            navigate({
              to: "/training/review/$progressId",
              params: {
                progressId: item.progressId,
              },
            });
          }}
        >
          Review
        </Button>
      ) : type === "approved" ? (
        <Button
          variant="secondary"
          size="sm"
          fullWidth={false}
          className="ml-auto"
          onClick={() => {
            navigate({
              to: "/training/review/$progressId",
              params: {
                progressId: item.progressId,
              },
            });
          }}
        >
          View
        </Button>
      ) : (
        <MessageMentor mentor={item.mentor} className="ml-auto" />
      )}
    </div>
  );
}
